import { getUnpublishStatus, SCHEDULE_STATUS } from "../constants/scheduleConstants";
import { ScheduleValue } from "./scheduleValidation";

/**
 * One call to the publish mutation. `status` is the word the mutation expects for this content
 * family; `isSchedule` false means "do it now" and carries no time.
 */
export type PublishStep = {
  status: string;
  isSchedule: boolean;
  scheduleDateTime?: string;
};

const publishNow = (): PublishStep => ({ status: SCHEDULE_STATUS.PUBLISH, isSchedule: false });

/**
 * Decides which publish calls a schedule needs. Mirrors the Pages rules in
 * `EditPageContainer.publishPage`:
 *
 * - nothing ticked                      -> publish now
 * - publish only                        -> schedule the publish
 * - unpublish only                      -> publish NOW, then schedule the unpublish
 * - publish and unpublish               -> schedule both
 *
 * The mutation takes one `status` per call, so two ticked boxes mean two sequential calls.
 */
export const planPublishSteps = (contentType: string, schedule: ScheduleValue): PublishStep[] => {
  const publishAt = schedule.isSchedulePublish ? schedule.schedulePublishDateTime : null;
  const unpublishAt = schedule.isScheduleUnpublish ? schedule.scheduleUnpublishDateTime : null;

  const steps: PublishStep[] = [];

  if (publishAt) {
    steps.push({
      status: SCHEDULE_STATUS.PUBLISH,
      isSchedule: true,
      scheduleDateTime: publishAt,
    });
  } else {
    steps.push(publishNow());
  }

  if (unpublishAt) {
    steps.push({
      status: getUnpublishStatus(contentType),
      isSchedule: true,
      scheduleDateTime: unpublishAt,
    });
  }

  return steps;
};

/**
 * Applies a step to the base input a call site already builds (`{ page }` for fixed types,
 * `{ document_name, document_id }` for schema types). A plain publish-now step returns the base
 * UNCHANGED, so an unscheduled publish stays byte-identical to what was sent before scheduling
 * existed.
 */
export const applyPublishStep = <T extends Record<string, unknown>>(
  base: T,
  step: PublishStep,
): T | (T & { status: string; is_schedule: true; schedule_date_time: string }) => {
  if (!step.isSchedule || !step.scheduleDateTime) return base;
  return {
    ...base,
    status: step.status,
    is_schedule: true,
    schedule_date_time: step.scheduleDateTime,
  };
};

/** Thrown when a later publish step fails after an earlier one already took effect. */
export class SchedulePartialFailureError extends Error {
  completedSteps: number;

  cause: unknown;

  constructor(completedSteps: number, cause: unknown) {
    super("A later schedule step failed after an earlier one had already been applied");
    this.name = "SchedulePartialFailureError";
    this.completedSteps = completedSteps;
    this.cause = cause;
  }
}

/**
 * Runs the steps in order. If the FIRST step fails nothing has happened, so its error propagates as
 * is. If a LATER step fails, an earlier one is already live (there is no rollback while cancel is
 * unavailable), so a {@link SchedulePartialFailureError} tells the caller to say so rather than let
 * the editor resubmit straight into "already trigger is initiated".
 */
export const runPublishSteps = async (
  steps: PublishStep[],
  send: (step: PublishStep) => Promise<unknown>,
): Promise<void> => {
  for (let index = 0; index < steps.length; index += 1) {
    try {
      // Steps are deliberately sequential: the backend serialises per item and the second call
      // must not start before the first has been accepted.
      // eslint-disable-next-line no-await-in-loop
      await send(steps[index]);
    } catch (error) {
      if (index === 0) throw error;
      throw new SchedulePartialFailureError(index, error);
    }
  }
};
